import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// --- Types ---

export interface Lead {
  id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  email?: string;
  emails?: string[];
  phone?: string;
  status: string;
  source?: string;
  notes?: string;
  assigned_consultants?: string[];
  created_by?: string;
  creator?: { name: string };
  [key: string]: any;
}

export interface Student {
  id: string;
  first_name: string;
  last_name: string;
  [key: string]: any;
}

export interface Proposal {
  id: string;
  lead_id: string;
  status: string;
  total_amount: number;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  consultant_name?: string;
  lead?: { branch_id?: string };
  [key: string]: any;
}

// --- Hooks ---

export const useLeads = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchLeads = useCallback(async (options?: {
    page?: number;
    itemsPerPage?: number;
    search?: string;
    status?: string;
    meetingType?: string;
    branchId?: string;
    assignedTo?: string;
    creatorId?: string;
    program?: string;
    registrationYears?: string[];
    createdFrom?: string;
    createdTo?: string;
  }) => {
    setLoading(true);
    try {
      // Build the base query
      let query = supabase
        .from('leads')
        .select(`
          *,
          creator:profiles!leads_created_by_fkey(name)
        `, { count: 'exact' });

      // Apply filters
      // Apply filters
      // Apply filters
      if (options?.search) {
        const term = options.search.trim();

        if (term) {
          // Search term parts
          const parts = term.split(/\s+/).filter(Boolean);

          // Construct the OR query string carefully
          // We search: First Name, Last Name, Phone
          // Note: 'email' column does not exist (migrated to emails array), removing to prevent 400 error.
          let searchConditions = [
            `first_name.ilike.%${term}%`,
            `last_name.ilike.%${term}%`,
            `phone.ilike.%${term}%`,
            // Search in student_info JSON
            `student_info->>first_name.ilike.%${term}%`,
            `student_info->>last_name.ilike.%${term}%`
          ];

          // If multiple parts (e.g. "Ad Soyad"), add combination search
          if (parts.length >= 2) {
            const p1 = parts[0];
            const p2 = parts.slice(1).join(' ');
            // Add "First Last" and "Last First" for Lead
            searchConditions.push(`and(first_name.ilike.%${p1}%,last_name.ilike.%${p2}%)`);
            searchConditions.push(`and(last_name.ilike.%${p1}%,first_name.ilike.%${p2}%)`);

            // Add "First Last" and "Last First" for Student Info
            searchConditions.push(`and(student_info->>first_name.ilike.%${p1}%,student_info->>last_name.ilike.%${p2}%)`);
            searchConditions.push(`and(student_info->>last_name.ilike.%${p1}%,student_info->>first_name.ilike.%${p2}%)`);
          }

          // Join with comma for OR logic
          query = query.or(searchConditions.join(','));
        }
      }

      if (options?.status && options.status !== 'all') {
        query = query.eq('status', options.status);
      }

      if (options?.meetingType && options.meetingType !== 'all') {
        query = query.eq('meeting_type', options.meetingType);
      }

      if (options?.branchId) {
        query = query.eq('branch_id', options.branchId);
      }

      if (options?.assignedTo) {
        query = query.contains('assigned_consultants', [options.assignedTo]);
      }

      if (options?.creatorId) {
        query = query.eq('created_by', options.creatorId);
      }

      if (options?.program && options?.program !== 'all') {
        query = query.contains('interested_programs', [options.program]);
      }

      if (options?.registrationYears && options.registrationYears.length > 0) {
        query = query.in('registration_year', options.registrationYears);
      }

      if (options?.createdFrom) {
        query = query.gte('created_at', options.createdFrom);
      }

      if (options?.createdTo) {
        query = query.lte('created_at', options.createdTo);
      }

      // Check if pagination is requested
      if (options?.page !== undefined && options?.itemsPerPage !== undefined) {
        // Server-side pagination
        const from = (options.page - 1) * options.itemsPerPage;
        const to = from + options.itemsPerPage - 1;

        query = query.order('created_at', { ascending: false }).range(from, to);

        const { data, count, error } = await query;
        if (error) throw error;

        setLeads(data || []);
        setTotalCount(count || 0);
        setTotalPages(Math.ceil((count || 0) / options.itemsPerPage));
      } else {
        // Parallel Fetch ALL Logic (Legacy/Dashboard support)
        // Note: For Dashboard, useDashboardStats is preferred to avoid heavy data load
        const { count, error: countError } = await supabase.from('leads').select('*', { count: 'exact', head: true });
        if (countError) throw countError;

        const total = count || 0;
        setTotalCount(total);
        const pageSize = 1000;
        const totalP = Math.ceil(total / pageSize);
        setTotalPages(totalP);

        if (totalP === 0) {
          setLeads([]);
          setLoading(false);
          return;
        }

        const promises = [];
        for (let i = 0; i < totalP; i++) {
          promises.push(
            supabase.from('leads').select(`*, creator:profiles!leads_created_by_fkey(name)`)
              .order('created_at', { ascending: false })
              .range(i * pageSize, (i + 1) * pageSize - 1)
          );
        }

        const results = await Promise.all(promises);
        let allLeads: Lead[] = [];
        for (const result of results) {
          if (result.error) throw result.error;
          if (result.data) allLeads = [...allLeads, ...result.data];
        }
        setLeads(allLeads);
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const addLead = async (leadData: any) => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .insert([leadData])
        .select()
        .single();

      if (error) throw error;

      setLeads(prev => [data, ...prev]);
      return { data, error: null };
    } catch (err: any) {
      console.error('Error adding lead:', err);
      return { data: null, error: err };
    }
  };

  const updateLead = async (id: string, updates: any) => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setLeads(prev => prev.map(l => l.id === id ? { ...l, ...data } : l));

      if (updates.status) {
        window.dispatchEvent(new CustomEvent('leadStatusUpdated', {
          detail: { leadId: id, newStatus: updates.status, source: 'manual' }
        }));
      }

      return { data, error: null };
    } catch (err: any) {
      console.error('Error updating lead:', err);
      return { data: null, error: err };
    }
  };

  const deleteLead = async (id: string) => {
    return deleteLeadDirect(id);
  };

  return { leads, loading, error, totalCount, totalPages, fetchLeads, addLead, updateLead, deleteLead };
};

export const deleteLeadDirect = async (id: string) => {
  try {
    const { error } = await supabase
      .from('leads')
      .delete()
      .eq('id', id)
      .select('id')
      .single();

    if (error) throw error;
    return { error: null };
  } catch (err: any) {
    console.error('Error deleting lead:', err);
    return { error: err };
  }
}

export const useDashboardStats = (userId?: string, role?: string, branchId?: string) => {
  const [stats, setStats] = useState<any>({
    totalLeads: 0,
    contactedLeads: 0,
    globalEnrolled: 0,
    conversionRate: 0,
    addedMonth: 0,
    addedWeek: 0,
    enrolledMonth: 0,
    avgEnrollment: '€0',
    revenue: 0,
    monthlyStats: [],
    topPrograms: [],
    topCountries: [],
    todayFollowUps: [] as any[]
  });
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      // Start of Week (Monday) calculation
      const d = new Date(now);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
      const monday = new Date(d.setDate(diff));
      monday.setHours(0, 0, 0, 0);
      const startOfWeek = monday.toISOString();
      const todayDate = new Date().toISOString().split('T')[0];
      const todayStart = todayDate + 'T00:00:00';
      const todayEnd = todayDate + 'T23:59:59';

      let filterString = '';
      if (role !== 'admin') {
        // assigned or created
        filterString = `assigned_consultants.cs.{${userId}},created_by.eq.${userId}`;
        if (branchId) {
          filterString += `,branch_id.eq.${branchId}`;
        }
      }

      // 1. Fetch Metadata for ALL leads (to calc counts)
      // We loop to get all records because Supabase limits to 1000 by default.
      let allMeta: any[] = [];
      let totalCount = 0;
      const pageSize = 1000;

      // First fetch to get count and first batch
      let queryFirst = supabase
        .from('leads')
        .select('id, status, created_at, updated_at, interested_programs, interested_countries', { count: 'exact' })
        .range(0, pageSize - 1);

      if (filterString) queryFirst = queryFirst.or(filterString);

      const { data: firstBatch, count: tCount, error: firstError } = await queryFirst;
      if (firstError) throw firstError;

      if (firstBatch) allMeta = [...firstBatch];
      totalCount = tCount || 0;

      // Calculate total pages needed
      const totalPages = Math.ceil(totalCount / pageSize);

      // Fetch remaining pages in parallel if there are more
      if (totalPages > 1) {
        const promises = [];
        for (let i = 1; i < totalPages; i++) {
          let queryNext = supabase
            .from('leads')
            .select('id, status, created_at, updated_at, interested_programs, interested_countries')
            .range(i * pageSize, (i + 1) * pageSize - 1);

          if (filterString) queryNext = queryNext.or(filterString);
          promises.push(queryNext);
        }

        const results = await Promise.all(promises);
        results.forEach(res => {
          if (res.data) allMeta = [...allMeta, ...res.data];
        });
      }

      const meta = allMeta; // Use the full list

      // 2. Fetch specific lists (FollowUps) with Full Data
      let queryFollowUps = supabase
        .from('leads')
        .select(`*, creator:profiles!leads_created_by_fkey(name)`)
        .eq('follow_up_required', true)
        .gte('follow_up_date', todayStart)
        .lte('follow_up_date', todayEnd);

      if (filterString) queryFollowUps = queryFollowUps.or(filterString);

      // 3. Proposals for Avg Amount
      const queryProposals = supabase
        .from('proposals')
        .select(`total_amount, created_by, lead:leads!inner(assigned_consultants)`)
        .eq('status', 'accepted')
        .gte('created_at', new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString()); // Last 1 year for revenue

      const [resFollowUps, resProposals] = await Promise.all([
        queryFollowUps,
        queryProposals
      ]);

      // Calculate Metrics
      const totalLeads = totalCount; // Use the exact count from DB
      const contactedLeads = meta.filter(l => l.status === 'contacted').length;
      const globalEnrolled = meta.filter(l => l.status === 'enrolled').length;

      const conversionRate = totalLeads > 0 ? (globalEnrolled / totalLeads) * 100 : 0;

      const addedMonth = meta.filter(l => l.created_at >= startOfMonth).length;
      const addedWeek = meta.filter(l => l.created_at >= startOfWeek).length;

      // Enrolled This Month (updated_at or created_at logic)
      const enrolledMonth = meta.filter(l => {
        if (l.status !== 'enrolled') return false;
        const date = l.updated_at || l.created_at;
        return date >= startOfMonth;
      }).length;

      // Avg Amount & Revenue
      let totalAmount = 0;
      let countProposals = 0;
      resProposals.data?.forEach((p: any) => {
        const isCreator = p.created_by === userId;
        const isAssigned = p.lead?.assigned_consultants?.includes(userId);
        if (role === 'admin' || isCreator || isAssigned) {
          totalAmount += Number(p.total_amount) || 0;
          countProposals++;
        }
      });
      const avg = countProposals > 0 ? totalAmount / countProposals : 0;


      // --- Analytics Calculations ---

      // 1. Monthly Stats (Last 6 Months)
      const monthlyStatsMap = new Map<string, { month: string, students: number, registrations: number }>();
      const monthNames = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Ekim', 'Kas', 'Ara'];

      // Initialize last 6 months
      for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        const label = monthNames[d.getMonth()];
        monthlyStatsMap.set(key, { month: label, students: 0, registrations: 0 });
      }

      meta.forEach(l => {
        const d = new Date(l.created_at);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        if (monthlyStatsMap.has(key)) {
          const entry = monthlyStatsMap.get(key)!;
          entry.students += 1; // Count all leads as leads
          if (l.status === 'enrolled') {
            entry.registrations += 1; // Count only enrolled as registrations
          }
        }
      });
      const monthlyStats = Array.from(monthlyStatsMap.values());

      // 2. Top Programs
      const programCounts: Record<string, number> = {};
      meta.forEach(l => {
        if (l.interested_programs && Array.isArray(l.interested_programs)) {
          l.interested_programs.forEach((p: string) => {
            if (p) programCounts[p] = (programCounts[p] || 0) + 1;
          });
        }
      });
      const topPrograms = Object.entries(programCounts)
        .map(([program, count]) => ({ program, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // 3. Top Countries
      const countryCounts: Record<string, number> = {};
      meta.forEach(l => {
        if (l.interested_countries && Array.isArray(l.interested_countries)) {
          l.interested_countries.forEach((c: string) => {
            if (c) countryCounts[c] = (countryCounts[c] || 0) + 1;
          });
        }
      });
      const topCountries = Object.entries(countryCounts)
        .map(([country, count]) => ({ country, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);


      setStats({
        totalLeads,
        contactedLeads,
        globalEnrolled,
        conversionRate,
        addedMonth,
        addedWeek,
        enrolledMonth,
        avgEnrollment: new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(avg),
        revenue: totalAmount,
        monthlyStats,
        topPrograms,
        topCountries,
        todayFollowUps: resFollowUps.data || []
      });

    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, role, branchId]);

  return { stats, loading, fetchStats };
};

export const useStudents = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setStudents(data || []);
    } catch (err) {
      console.error('Error fetching students:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { students, loading, fetchStudents };
};

export const useProposals = () => {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select('*, lead:leads(branch_id)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProposals(data || []);
    } catch (err) {
      console.error('Error fetching proposals:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { proposals, loading, fetchProposals };
};

export const useDeadlines = () => {
  const [deadlines, setDeadlines] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDeadlines = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('deadlines') // Check table name if it's 'deadlines' or 'calendar_events' or similar
        .select('*')
        .order('due_date', { ascending: true });

      if (error) throw error;
      setDeadlines(data || []);
    } catch (err) {
      console.error("Error fetching deadlines", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const addDeadline = async (deadlineData: any) => {
    try {
      const { data, error } = await supabase.from('deadlines').insert([deadlineData]).select().single();
      if (error) throw error;
      setDeadlines(prev => [...prev, data]);
      return { data, error: null };
    } catch (err: any) {
      return { data: null, error: err };
    }
  };

  const deleteDeadline = async (id: string) => {
    try {
      const { error } = await supabase.from('deadlines').delete().eq('id', id);
      if (error) throw error;
      setDeadlines(prev => prev.filter(d => d.id !== id));
      return { error: null };
    } catch (err: any) {
      return { error: err };
    }
  }

  return { deadlines, loading, fetchDeadlines, addDeadline, deleteDeadline };
};

export const useReminders = () => {
  const [reminders, setReminders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchReminders = useCallback(async (leadId?: string) => {
    setLoading(true);
    try {
      let query = supabase.from('reminders').select('*').order('date', { ascending: true });

      if (leadId) {
        query = query.eq('lead_id', leadId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setReminders(data || []);
    } catch (err) {
      console.error('Error fetching reminders:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const addReminder = async (reminderData: any) => {
    try {
      const { data, error } = await supabase.from('reminders').insert([reminderData]).select().single();
      if (error) throw error;
      setReminders(prev => [...prev, data]);
      return { data, error: null };
    } catch (err: any) {
      return { data: null, error: err };
    }
  };

  const updateReminder = async (id: string, updates: any) => {
    try {
      const { data, error } = await supabase.from('reminders').update(updates).eq('id', id).select().single();
      if (error) throw error;
      setReminders(prev => prev.map(r => r.id === id ? { ...r, ...data } : r));
      return { data, error: null };
    } catch (err: any) {
      return { data: null, error: err };
    }
  };

  const deleteReminder = async (id: string) => {
    try {
      const { error } = await supabase.from('reminders').delete().eq('id', id);
      if (error) throw error;
      setReminders(prev => prev.filter(r => r.id !== id));
      return { error: null };
    } catch (err: any) {
      return { error: err };
    }
  };

  return { reminders, loading, fetchReminders, addReminder, updateReminder, deleteReminder };
}
